(function initialiseIslanderMotionLoader(globalScope, documentScope) {
  const motionSelector = '[data-islander-motion]';
  const scriptPromises = new Map();
  const loggedSources = new Set();
  const motionPreference = globalScope.matchMedia('(prefers-reduced-motion: reduce)');
  let refreshFrame = null;
  let preferenceChangeQueue = Promise.resolve();

  function getMotionRoots(scope) {
    if (!scope) return [];

    const roots = new Set();
    if (scope.matches?.(motionSelector)) roots.add(scope);
    scope.querySelectorAll?.(motionSelector).forEach((root) => roots.add(root));
    return Array.from(roots);
  }

  function getAbsoluteSource(source) {
    if (!source) throw new Error('A motion asset source is missing.');
    return new URL(source, documentScope.baseURI).href;
  }

  function loadScript(source) {
    let absoluteSource;
    try {
      absoluteSource = getAbsoluteSource(source);
    } catch (error) {
      error.source = String(source || 'unknown');
      return Promise.reject(error);
    }

    if (scriptPromises.has(absoluteSource)) return scriptPromises.get(absoluteSource);

    const promise = new Promise((resolve, reject) => {
      const script = documentScope.createElement('script');
      script.async = true;
      script.src = absoluteSource;
      script.onload = () => {
        script.onload = null;
        script.onerror = null;
        resolve(absoluteSource);
      };
      script.onerror = () => {
        const error = new Error(`Failed to load motion asset: ${absoluteSource}`);
        error.source = absoluteSource;
        script.onload = null;
        script.onerror = null;
        reject(error);
      };
      documentScope.head.appendChild(script);
    });

    scriptPromises.set(absoluteSource, promise);
    return promise;
  }

  function getUniqueSources(roots, datasetKey) {
    const sources = new Map();
    roots.forEach((root) => {
      const source = root.dataset[datasetKey];
      const absoluteSource = getAbsoluteSource(source);
      if (!sources.has(absoluteSource)) sources.set(absoluteSource, source);
    });
    return Array.from(sources.values());
  }

  function loadSources(roots, datasetKey) {
    return Promise.all(getUniqueSources(roots, datasetKey).map(loadScript));
  }

  async function loadBaseDependencies(roots) {
    await loadSources(roots, 'islanderMotionUtilsSrc');
    await loadSources(roots, 'islanderMotionRuntimeSrc');
  }

  async function loadEngines(roots) {
    await loadSources(roots, 'islanderMotionGsapSrc');
    await loadSources(roots, 'islanderMotionScrollTriggerSrc');
  }

  function configureRuntime(roots) {
    const runtime = globalScope.IslanderMotion;
    if (!runtime) {
      const error = new Error('The Islander motion runtime did not initialise.');
      error.source = getAbsoluteSource(roots[0].dataset.islanderMotionRuntimeSrc);
      throw error;
    }

    runtime.configure({
      gsap: motionPreference.matches ? null : globalScope.gsap,
      ScrollTrigger: motionPreference.matches ? null : globalScope.ScrollTrigger,
      reducedMotion: motionPreference.matches,
    });
    return runtime;
  }

  function revealFallbacks(roots) {
    roots.forEach((root) => root.classList.remove('is-motion-loading'));
  }

  function reportFailure(error) {
    const source = error.source || 'unknown';
    if (!globalScope.Shopify?.designMode || loggedSources.has(source)) return;

    loggedSources.add(source);
    console.error('Islander motion asset failed:', source, error);
  }

  async function initialiseScope(scope) {
    const roots = getMotionRoots(scope);
    if (roots.length === 0) return;

    try {
      await loadBaseDependencies(roots);
      if (!motionPreference.matches) await loadEngines(roots);
      const runtime = configureRuntime(roots);
      await loadSources(roots, 'islanderMotionControllerSrc');
      runtime.init(scope);
    } catch (error) {
      revealFallbacks(roots);
      reportFailure(error);
    }
  }

  async function rebuildForMotionPreference() {
    const roots = getMotionRoots(documentScope);
    if (roots.length === 0) return;

    try {
      await loadBaseDependencies(roots);
      if (!motionPreference.matches) await loadEngines(roots);
      globalScope.IslanderMotion?.destroy(documentScope);
      const runtime = configureRuntime(roots);
      await loadSources(roots, 'islanderMotionControllerSrc');
      runtime.init(documentScope);
    } catch (error) {
      revealFallbacks(roots);
      reportFailure(error);
    }
  }

  function scheduleRefresh() {
    if (refreshFrame) return;
    refreshFrame = globalScope.requestAnimationFrame(() => {
      refreshFrame = null;
      globalScope.IslanderMotion?.refresh();
    });
  }

  documentScope.addEventListener('shopify:section:load', (event) => initialiseScope(event.target));
  documentScope.addEventListener('shopify:section:unload', (event) => {
    globalScope.IslanderMotion?.destroy(event.target);
  });
  documentScope.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(motionSelector);
    if (root) globalScope.IslanderMotion?.selectBlock(root, event.detail.blockId);
  });
  documentScope.addEventListener('shopify:section:reorder', scheduleRefresh);
  globalScope.addEventListener('resize', scheduleRefresh, { passive: true });
  globalScope.addEventListener('orientationchange', scheduleRefresh, { passive: true });
  motionPreference.addEventListener('change', () => {
    preferenceChangeQueue = preferenceChangeQueue.then(rebuildForMotionPreference, rebuildForMotionPreference);
  });

  if (documentScope.readyState === 'loading') {
    documentScope.addEventListener('DOMContentLoaded', () => initialiseScope(documentScope), { once: true });
  } else {
    initialiseScope(documentScope);
  }
})(window, document);
